
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { type FeedbackStatus } from "@/types";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { Roadmap } from "@/types";

type Feedback = {
  id: string;
  title: string;
  description: string;
  status: FeedbackStatus;
  votes: number;
  roadmap_id?: string;
};

type RoadmapBoardProps = {
  feedback: Feedback[];
  companyStatuses?: any[];
  onFeedbackUpdate: () => void;
  isReadOnly?: boolean;
  roadmap?: Roadmap | null;
};

const defaultStatusLabels: Record<FeedbackStatus, string> = {
  in_review: "In Review",
  planned: "Planned",
  in_progress: "In Progress",
  completed: "Completed",
  rejected: "Rejected"
};

// Map from company status name to feedback status enum value
const getEnumStatusFromName = (name: string): FeedbackStatus => {
  const normalizedName = name.toLowerCase().replace(/\s+/g, '_');
  
  switch (normalizedName) {
    case 'in_review':
      return 'in_review';
    case 'planned':
      return 'planned';
    case 'in_progress':
      return 'in_progress';
    case 'completed':
      return 'completed';
    case 'rejected':
      return 'rejected';
    default:
      return 'in_review'; // Default fallback
  }
};

const RoadmapBoard = ({ 
  feedback, 
  companyStatuses = [],
  onFeedbackUpdate, 
  isReadOnly = false,
  roadmap = null
}: RoadmapBoardProps) => {
  const { toast } = useToast();
  const [items, setItems] = useState<Feedback[]>(feedback);
  const [statusMap, setStatusMap] = useState<Record<string, any>>({});
  const [statusIdToEnumMap, setStatusIdToEnumMap] = useState<Record<string, FeedbackStatus>>({});

  useEffect(() => {
    setItems(feedback);
  }, [feedback]);

  // Create mappings from status ID to status object and to enum value
  useEffect(() => {
    const map: Record<string, any> = {};
    const idToEnumMap: Record<string, FeedbackStatus> = {};
    
    companyStatuses.forEach(status => {
      map[status.id] = status;
      
      // Map the company status ID to the corresponding enum value
      const enumStatus = getEnumStatusFromName(status.name);
      idToEnumMap[status.id] = enumStatus;
    });
    
    setStatusMap(map);
    setStatusIdToEnumMap(idToEnumMap);
  }, [companyStatuses]);

  const filteredFeedback = roadmap 
    ? items.filter(item => !item.roadmap_id || item.roadmap_id === roadmap.id)
    : items;

  const getFeedbackByStatus = (statusId: string) => {
    // If we have a roadmap with specific statuses
    if (roadmap && roadmap.statuses) {
      // Find roadmap status that matches company status ID or name
      const roadmapStatus = roadmap.statuses.find(rs => {
        const companyStatus = companyStatuses.find(cs => cs.id === statusId);
        return companyStatus && rs.name.toLowerCase() === companyStatus.name.toLowerCase();
      });

      if (roadmapStatus) {
        // Return feedback with matching status by name or ID
        return filteredFeedback.filter(item => {
          if (typeof item.status === 'string') {
            // Check if status matches the status ID directly
            if (item.status === statusId) return true;
            
            // Or check if status matches by name
            const normalizedItemStatus = item.status.toLowerCase().replace(/\s+/g, '_');
            const normalizedStatusName = roadmapStatus.name.toLowerCase().replace(/\s+/g, '_');
            return normalizedItemStatus === normalizedStatusName;
          }
          return false;
        });
      }
      return [];
    } 
    
    // Default behavior - just match by status ID
    return filteredFeedback.filter(item => item.status === statusId);
  };

  const handleDragEnd = async (result: any) => {
    if (isReadOnly) return;
    
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      // No status change if dropped in the same column
      return;
    }

    const newStatusId = destination.droppableId;
    
    try {
      // Find the correct enum value for the status
      let statusEnumValue: FeedbackStatus;
      
      // If this is a company status ID, get the corresponding enum value
      if (statusIdToEnumMap[newStatusId]) {
        statusEnumValue = statusIdToEnumMap[newStatusId];
      } else {
        // If it's a custom status or direct enum value, normalize it
        const companyStatus = companyStatuses.find(s => s.id === newStatusId);
        
        if (companyStatus) {
          statusEnumValue = getEnumStatusFromName(companyStatus.name);
        } else {
          // Try to use it directly if it matches an enum value
          statusEnumValue = newStatusId as FeedbackStatus;
        }
      }
      
      // Update local state first for immediate feedback
      const updatedItems = items.map(item => 
        item.id === draggableId ? { 
          ...item, 
          status: statusEnumValue,
          roadmap_id: roadmap?.id || item.roadmap_id
        } : item
      );
      setItems(updatedItems);

      const updateData: any = { 
        status: statusEnumValue
      };
      
      if (roadmap) {
        updateData.roadmap_id = roadmap.id;
      }

      console.log("Updating feedback with data:", updateData);

      // Update in database
      const { error } = await supabase
        .from('feedback')
        .update(updateData)
        .eq('id', draggableId);

      if (error) {
        console.error("Error updating feedback status:", error);
        // Revert local state on error
        setItems(feedback);
        toast({
          title: "Error",
          description: "Failed to update feedback status",
          variant: "destructive"
        });
        return;
      }

      // Notify parent component to refresh data
      onFeedbackUpdate();
      
      // Get status label from company statuses
      let statusLabel = "";
      if (statusMap[newStatusId]) {
        statusLabel = statusMap[newStatusId].name;
      } else {
        // Fallback for legacy statuses
        statusLabel = defaultStatusLabels[statusEnumValue] || 
                     statusEnumValue.replace('_', ' ');
      }
      
      toast({
        title: "Success",
        description: `Feedback moved to ${statusLabel}`
      });
    } catch (err) {
      console.error("Unexpected error in handleDragEnd:", err);
      setItems(feedback);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  const getStatusColumns = () => {
    if (roadmap && roadmap.statuses && roadmap.statuses.length > 0) {
      // Map roadmap statuses to company statuses
      return roadmap.statuses
        .sort((a, b) => a.position - b.position)
        .map(roadmapStatus => {
          // Find matching company status
          const companyStatus = companyStatuses.find(
            cs => cs.name.toLowerCase() === roadmapStatus.name.toLowerCase()
          );
          
          return {
            id: companyStatus?.id || roadmapStatus.name.toLowerCase().replace(/\s+/g, '_'),
            name: roadmapStatus.name,
            color: roadmapStatus.color
          };
        });
    } else if (companyStatuses && companyStatuses.length > 0) {
      // Use company statuses if available
      return companyStatuses
        .sort((a, b) => a.position - b.position)
        .map(status => ({
          id: status.id,
          name: status.name,
          color: status.color
        }));
    } else {
      // Fallback to default statuses
      return [
        { id: "in_review", name: "In Review", color: "#9333ea" },
        { id: "planned", name: "Planned", color: "#3b82f6" },
        { id: "in_progress", name: "In Progress", color: "#eab308" },
        { id: "completed", name: "Completed", color: "#22c55e" },
        { id: "rejected", name: "Rejected", color: "#ef4444" }
      ];
    }
  };

  const statusColumns = getStatusColumns();

  const Board = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 overflow-x-auto p-4">
      {statusColumns.map((status) => (
        <div key={status.id} className="min-w-[250px]">
          <h3 className="font-medium mb-4 text-center flex items-center justify-center gap-2">
            <span 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: status.color }}
            />
            {status.name}
          </h3>
          <Droppable droppableId={status.id} isDropDisabled={isReadOnly}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-4 min-h-[200px]"
              >
                {getFeedbackByStatus(status.id).map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id}
                    index={index}
                    isDragDisabled={isReadOnly}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`transition-shadow ${
                          snapshot.isDragging ? "shadow-lg" : ""
                        }`}
                      >
                        <Card className={`p-4 bg-card ${!isReadOnly ? 'hover:bg-accent/50 cursor-grab active:cursor-grabbing' : ''}`}>
                          <h4 className="font-medium mb-2">{item.title}</h4>
                          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                            {item.description}
                          </p>
                          <div className="flex items-center justify-end">
                            <span className="text-sm font-medium">
                              {item.votes} votes
                            </span>
                          </div>
                        </Card>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      ))}
    </div>
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Board />
    </DragDropContext>
  );
};

export default RoadmapBoard;
